<template>
  <div class="courses-page px-3 pb-10">
    <div class="flex flex-col items-center justify-center mb-6">
      <div class="text-3xl font-medium !mt-0">Мои курсы</div>
      <div class="text-gray-600">Изучайте новые навыки и проходите тесты</div>
    </div>

    <div v-if="isLoading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Загрузка курсов...</p>
    </div>

    <div v-else-if="activeCourses.length === 0 && completedCourses.length === 0" class="empty-state">
      <div class="empty-icon">
        <svg class="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
        </svg>
      </div>
      <div class="empty-title">Курсы не найдены</div>
      <p class="empty-text">У вас пока нет доступных курсов для изучения</p>
    </div>

    <div v-else class="space-y-8">
      <!-- Активные курсы -->
      <div v-if="activeCourses.length > 0" class="course-section">
        <div class="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          Активные курсы
        </div>
        <div class="courses-grid">
          <div 
            v-for="course in activeCourses" 
            :key="course.id" 
            class="course-card"
            @click="openCourse(course)"
          >
            <!-- Статус выполнения -->
            <div class="course-status">
              <div class="status-badge in-progress">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
<!--                В процессе-->
              </div>
            </div>

            <!-- Содержимое карточки -->
            <div class="course-content">
              <div class="course-title">{{ course.title }}</div>
              <p class="course-description">{{ course.description || 'Описание курса отсутствует' }}</p>
              <div v-if="course.dueAt" class="deadline-badge mt-2" :class="deadlineClassName(course.dueAt)">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span>Дедлайн: {{ formatDate(course.dueAt) }}</span>
              </div>
              
              
              <!-- Статистика -->
              <div class="course-stats">
                <div class="stat-item">
                  <svg class="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                  </svg>
                  <span>{{ course.lessonsCount || 0 }} уроков</span>
                </div>
                <div class="stat-item">
                  <svg class="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                  </svg>
                  <span>{{ course.testsCount || 0 }} тестов</span>
                </div>
              </div>
            </div>

            <!-- Действия -->
            <div class="course-actions" @click.stop>
              <button 
                class="action-btn primary"
                @click="openCourse(course)"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h8m-10 5h10a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v11a2 2 0 002 2z"/>
                </svg>
                Открыть курс
              </button>
              
              <button
                v-if="course.testsCount > 0"
                class="action-btn secondary"
                @click="openTestFromCourse(course)"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Пройти тест
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Пройденные курсы -->
      <div v-if="completedCourses.length > 0" class="course-section">
        <div class="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <svg class="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          Завершенные курсы
        </div>
        <div class="courses-grid">
          <div 
            v-for="course in completedCourses" 
            :key="course.id" 
            class="course-card completed"
            @click="openCourse(course)"
          >
            <!-- Статус выполнения -->
            <div class="course-status">
              <div class="status-badge completed">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
                {{ course.completedLate ? 'Завершен (просрочено)' : 'Завершен' }}
              </div>
            </div>

            <!-- Содержимое карточки -->
            <div class="course-content">
              <div class="course-title">{{ course.title }}</div>
              <p class="course-description">{{ course.description || 'Описание курса отсутствует' }}</p>
              
              <!-- Статистика -->
              <div class="course-stats">
                <div class="stat-item">
                  <svg class="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                  </svg>
                  <span>{{ course.lessonsCount || 0 }} уроков</span>
                </div>
                <div class="stat-item">
                  <svg class="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                  </svg>
                  <span>{{ course.testsCount || 0 }} тестов</span>
                </div>
              </div>
            </div>

            <!-- Действия -->
            <div class="course-actions" @click.stop>
              <button 
                class="action-btn primary"
                @click="openCourse(course)"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
                Просмотреть
              </button>
              
              <button 
                v-if="course.testsCount > 0"
                class="action-btn secondary"
                @click="openTestFromCourse(course)"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
                Повторить тест
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Модальные окна -->
    <UModal v-model="isCourseModalOpen" :fullscreen="true">
      <CourseViewModal 
        v-if="selectedCourse"
        :courseId="selectedCourse.id" 
        :openTestsTab="selectedCourse.openTestsTab || false"
        @close="closeCourseModal" 
        @openTest="handleOpenTestFromCourse"
      />
    </UModal>

    <UModal v-model="isTestInfoModalOpen">
      <TestInfoModal 
        v-if="selectedCourse"
        :courseId="selectedCourse.id"
        :testId="selectedTestId"
        @close="closeTestInfoModal"
        @startTest="startTestExecution"
        @openLessons="openLessonsFromWarning"
      />
    </UModal>

    <UModal v-model="isTestExecutionModalOpen">
      <TestExecutionModal 
        v-if="selectedCourse && testData"
        :testData="testData"
        :courseId="selectedCourse.id"
        @close="closeTestExecutionModal"
        @complete="onTestComplete"
      />
    </UModal>

    <UModal v-model="isTestResultModalOpen" :fullscreen="true">
      <TestResultModal 
        v-if="testResult"
        :result="testResult"
        :courseId="selectedCourse?.id"
        @close="closeTestResultModal"
        @retryTest="retryTest"
      />
    </UModal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import apiService from '@/services/api'
import UModal from '@/components/ui/UModal.vue'
import CourseViewModal from '@/components/course/CourseViewModal.vue'
import TestInfoModal from '@/components/course/TestInfoModal.vue'
import TestExecutionModal from '@/components/course/TestExecutionModal.vue'
import TestResultModal from '@/components/course/TestResultModal.vue'
import {usePortalStore} from "@/stores/portalStore";

const portalStore = usePortalStore()

const parseSearch = () => {
  try {
    return new URLSearchParams(window.location.search)
  } catch {
    return new URLSearchParams('')
  }
}

const courses = ref([])
const isLoading = ref(false)
const selectedCourse = ref(null)
const selectedTestId = ref(null)
const testData = ref(null)
const testResult = ref(null)

// Модальные окна
const isCourseModalOpen = ref(false)
const isTestInfoModalOpen = ref(false)
const isTestExecutionModalOpen = ref(false)
const isTestResultModalOpen = ref(false)

// Разделяем курсы на активные и завершенные
const activeCourses = computed(() => {
  return courses.value.filter(course => course.isActive && !course.isCompleted)
})

const completedCourses = computed(() => {
  return courses.value.filter(course => course.isActive && course.isCompleted)
})

const loadCourses = async () => {
  isLoading.value = true
  try {
    const response = await apiService.getCourses(true)
    courses.value = response.data || []
  } catch (error) {
    console.error('Ошибка при загрузке курсов:', error)
  } finally {
    isLoading.value = false
  }
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const d = new Date(dateString)
  if (Number.isNaN(d.getTime())) return ''
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${dd}.${mm}.${yyyy}`
}

const deadlineClassName = (dateString) => {
  const now = new Date()
  const d = new Date(dateString)
  if (Number.isNaN(d.getTime())) return 'deadline-ok'
  const msInDay = 24 * 60 * 60 * 1000
  const diff = Math.floor((d.setHours(23,59,59,999) - now.getTime()) / msInDay)
  if (diff < 0) return 'deadline-danger'
  if (diff <= 3) return 'deadline-warning'
  return 'deadline-ok'
}

const openCourse = (course) => {
  selectedCourse.value = course
  isCourseModalOpen.value = true
}

const openCourseAfterUpload = () => {
  const courseId = portalStore.portalData?.options?.course;

  if (courseId) {
    const course = courses.value.find((i) => i.id === courseId)

    if (course) {
      selectedCourse.value = course
      isCourseModalOpen.value = true
    }
  }
}

const openTestFromCourse = (course) => {
  selectedCourse.value = course
  // Открываем курс и передаем флаг для перехода на вкладку тестирования
  course.openTestsTab = true
  isCourseModalOpen.value = true
}

const closeCourseModal = () => {
  isCourseModalOpen.value = false
  selectedCourse.value = null
}

const closeTestInfoModal = () => {
  isTestInfoModalOpen.value = false
  selectedCourse.value = null
  selectedTestId.value = null
}

const startTestExecution = (data) => {
  testData.value = data
  isTestInfoModalOpen.value = false
  isTestExecutionModalOpen.value = true
}

const closeTestExecutionModal = () => {
  isTestExecutionModalOpen.value = false
  testData.value = null
  selectedCourse.value = null
}

const onTestComplete = (result) => {
  console.log('Тест завершен:', result)
  closeTestExecutionModal()
  testResult.value = result
  isTestResultModalOpen.value = true
}

const handleOpenTestFromCourse = (testData) => {
  selectedTestId.value = testData.testId
  isCourseModalOpen.value = false
  isTestInfoModalOpen.value = true
}

const closeTestResultModal = async () => {
  isTestResultModalOpen.value = false
  testResult.value = null
  selectedCourse.value = null
  await loadCourses()
}

const retryTest = () => {
  const currentTestResult = testResult.value

  closeTestResultModal()

  let course = selectedCourse.value
  if (!course && currentTestResult?.courseId) {
    course = courses.value.find((i) => i.id === currentTestResult.courseId)
  }
  selectedCourse.value = course || selectedCourse.value

  if (currentTestResult?.testId) {
    selectedTestId.value = currentTestResult.testId
  }

  isTestInfoModalOpen.value = true
}

const openLessonsFromWarning = () => {
  isTestInfoModalOpen.value = false
  isCourseModalOpen.value = true
}

onMounted(async () => {
  try {
    const qs = parseSearch()
    const courseIdFromQuery = qs.get('course') || qs.get('courseId') || qs.get('course_id') || ''
    if (courseIdFromQuery) {
      const currentOptions = portalStore.portalData?.options || {}
      portalStore.setPortalData({
        options: {
          ...currentOptions,
          course: String(courseIdFromQuery),
        },
      })
    }
  } catch (e) { void e }

  await loadCourses()

  openCourseAfterUpload()
})
</script>

<style scoped>
.courses-page {
  max-width: 1200px;
  margin: 0 auto;
}

.course-section {
  margin-bottom: 2rem;
}

.page-header {
  text-align: center;
  margin-bottom: 48px;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
}

.page-subtitle {
  font-size: 1.125rem;
  color: #6b7280;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 0;
  color: #6b7280;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-left: 4px solid #574AEA;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 0;
  text-align: center;
}

.empty-icon {
  margin-bottom: 24px;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.empty-text {
  color: #6b7280;
  font-size: 1rem;
}

.courses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
}

.course-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 24px;
  padding: 20px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.course-card.completed {
  background: linear-gradient(135deg, #f8fffe 0%, #f0fdf4 100%);
  border: 1px solid #d1fae5;
}

.course-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 32px -8px rgba(0, 0, 0, 0.15);
  border-color: transparent;
}

.course-card.completed:hover {
  box-shadow: 0 16px 32px -8px rgba(34, 197, 94, 0.15);
}

.course-status {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.status-badge.completed {
  background: #dcfce7;
  color: #166534;
}

.status-badge.in-progress {
  background: #fef3c7;
  color: #92400e;
}

.deadline-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.deadline-badge.deadline-ok {
  background: #eff6ff;
  color: #1d4ed8;
}

.deadline-badge.deadline-warning {
  background: #fef3c7;
  color: #92400e;
}

.deadline-badge.deadline-danger {
  background: #fee2e2;
  color: #991b1b;
}

.deadline-note {
  margin-top: 8px;
  font-size: 0.8rem;
  color: #991b1b;
  background: #fee2e2;
  padding: 6px 10px;
  border-radius: 8px;
  display: inline-block;
}

.course-content {
  margin-bottom: 20px;
  padding-right: 120px;
}

.course-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
  line-height: 1;
}

.course-description {
  font-size: 0.75rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.course-stats {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  color: #6b7280;
}

.course-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.8rem;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
}

.action-btn.primary {
  background: #574AEA;
  color: white;
}

.action-btn.primary:hover {
  background: #473add;
  transform: translateY(-1px);
}

.action-btn.secondary {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.action-btn.secondary:hover {
  background: #e5e7eb;
  transform: translateY(-1px);
}

@media (max-width: 768px) {
  .courses-page {
    padding: 12px;
    max-width: 100%;
  }
  
  .page-header {
    margin-bottom: 32px;
    padding: 0 4px;
  }
  
  .page-title {
    font-size: 2rem;
    line-height: 1.2;
  }
  
  .page-subtitle {
    font-size: 1rem;
    padding: 0 20px;
  }
  
  .course-section {
    margin-bottom: 1.5rem;
  }
  
  .courses-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .course-card {
    padding: 20px;
    border-radius: 16px;
    margin: 0 4px;
  }
  
  .course-content {
    padding-right: 0;
    margin-bottom: 16px;
  }
  
  .course-title {
    font-size: 1.125rem;
    margin-bottom: 6px;
  }
  
  .course-description {
    font-size: 0.8rem;
    margin-bottom: 12px;
    line-height: 1.4;
  }
  
  .course-status {
    position: static;
    margin-bottom: 12px;
    display: flex;
    justify-content: flex-start;
  }
  
  .status-badge {
    font-size: 0.7rem;
    padding: 4px 8px;
  }
  
  .course-stats {
    gap: 12px;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }
  
  .stat-item {
    font-size: 0.8rem;
    gap: 4px;
  }
  
  .course-actions {
    gap: 8px;
  }
  
  .action-btn {
    padding: 10px 16px;
    font-size: 0.875rem;
    border-radius: 12px;
    min-height: 44px; /* Минимальный размер для удобного тапа */
  }
  
  .loading-state, .empty-state {
    padding: 48px 16px;
  }
  
  .empty-title {
    font-size: 1.25rem;
    padding: 0 16px;
  }
  
  .empty-text {
    font-size: 0.875rem;
    padding: 0 16px;
  }
}

/* Дополнительные стили для очень маленьких экранов */
@media (max-width: 480px) {
  .courses-page {
    padding: 8px;
  }
  
  .page-title {
    font-size: 1.75rem;
  }
  
  .page-subtitle {
    font-size: 0.9rem;
  }
  
  .course-card {
    padding: 16px;
    margin: 0 2px;
  }
  
  .course-title {
    font-size: 1rem;
  }
  
  .course-description {
    font-size: 0.75rem;
  }
  
  .action-btn {
    padding: 12px 16px;
    font-size: 0.8rem;
  }
  
  .course-stats {
    flex-direction: column;
    gap: 8px;
  }
  
  .stat-item {
    font-size: 0.75rem;
  }
}
</style>
